import { useEffect, useRef, useState } from "react";
import type { DashboardData, OwnerFilter } from "../../types";
import { isExpense } from "../../lib/finance";
import { api } from "../../lib/api";
import { transactionDraft } from "./transaction-draft";

/** The uncategorised-expenses review: which are selected, and giving them all one category. */
export function useBulkCategorize({ data, owner, refresh, setError, reviewUncategorized }: {
  data: DashboardData | null;
  owner: OwnerFilter;
  refresh: (force?: boolean) => Promise<unknown>;
  setError: (message: string) => void;
  reviewUncategorized: boolean;
}) {
  const [categorySelection, setCategorySelection] = useState<string[]>([]);
  const [bulkCategory, setBulkCategory] = useState("");
  const [categorizing, setCategorizing] = useState(false);
  const categorizeLock = useRef(false);
  useEffect(() => {
    const url = new URL(window.location.href);
    if (reviewUncategorized) url.searchParams.set("categorize", "1");
    else url.searchParams.delete("categorize");
    window.history.replaceState(window.history.state, "", url);
  }, [reviewUncategorized]);
  useEffect(() => { setCategorySelection([]); }, [owner]);
  async function categorizeSelected() {
    if (!data || !bulkCategory || categorizeLock.current) return;
    categorizeLock.current = true;
    setCategorizing(true);
    setError("");
    let failure = "";
    try {
      // A few entries at a time rather than one after another: each update is independent and
      // guarded on its own, and a failure stops new ones from starting while leaving saved ones kept.
      const byId = new Map((data.transactions || []).map((t) => [t.id, t]));
      const queue = categorySelection.filter((id) => {
        const tx = byId.get(id);
        return tx && isExpense(tx) && !tx.toAccountId;
      });
      let stopped: unknown;
      const worker = async () => {
        for (let id = queue.shift(); id !== undefined && stopped === undefined; id = queue.shift()) {
          const tx = byId.get(id)!;
          try {
            await api("transactions/update", { id, expected: tx, expense: { ...transactionDraft(tx, []), trackedItems: undefined, category: tx.category === "Uncategorized" ? bulkCategory : tx.category, subcategory: tx.category === "Uncategorized" ? "" : tx.subcategory, allocations: transactionDraft(tx, []).allocations?.map(p => p.category === "Uncategorized" ? { ...p, category: bulkCategory, subcategory: "" } : p) } });
            setCategorySelection(ids => ids.filter(selected => selected !== id));
          } catch (e) { stopped ??= e; }
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker));
      if (stopped !== undefined) throw stopped;
    } catch (e) { failure = `Some transactions still need categorizing. Saved changes are kept. ${(e as Error).message}`; }
    finally { await refresh(true); if (failure) setError(failure); setCategorizing(false); categorizeLock.current = false; }
  }
  return {
    categorySelection, setCategorySelection,
    bulkCategory, setBulkCategory,
    categorizing, categorizeSelected,
  };
}
